import {
  Alert,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { readBackups } from '../api/endpoints';
import type { BackupOverview, BackupSet } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageHeader } from '../components/PageHeader';
import { SortableCell } from '../components/SortableCell';
import { StatTile } from '../components/StatTile';
import { TableFilters } from '../components/TableFilters';
import { TableSkeletonRows } from '../components/TableSkeletonRows';
import { sortRows, useSort } from '../components/useSort';

/** Columns this table can be ordered by. Sorted in the browser — the whole inventory is already here. */
type SortKey =
  'finishedAt' | 'type' | 'durationSeconds' | 'databaseBytes' | 'repositoryBytes' | 'compressionRatio';

const TYPE_LABEL: Record<string, string> = {
  full: 'úplná',
  diff: 'rozdielová',
  incr: 'prírastková',
};

const bytes = (value: number): string => {
  if (value < 1024) return `${value} B`;

  const units = ['kB', 'MB', 'GB', 'TB'];
  let size = value / 1024;
  let unit = 0;

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[unit]}`;
};

const moment = (value: string | null): string =>
  value === null ? '—' : new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * How long, in words a person reads rather than a number they convert.
 *
 * Seconds up to a minute, then minutes, then hours, then days. A recovery window of 1 209 600 seconds is
 * technically the answer and practically useless.
 */
const duration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;

  return `${Math.round(seconds / 86_400)} dní`;
};

const since = (value: string | null): string => {
  if (value === null) return '—';

  return duration(Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000)));
};

export const BackupsPage = () => {
  const [type, setType] = useState('');
  const sort = useSort<SortKey>('finishedAt', 'desc');
  const { data, error, isLoading, refetch } = useQuery<BackupOverview>({
    queryKey: ['backups'],
    queryFn: readBackups,
    // The repository is read by shelling out to pgBackRest and reaching an object store. Polling it as
    // often as a dashboard chart would be rude to the thing it is watching.
    refetchInterval: 60_000,
  });

  const repository = data?.repository ?? null;
  const status = data?.status ?? null;
  const window = repository?.recoveryWindow ?? null;

  const VALUE: Record<SortKey, (row: BackupSet) => string | number | null> = {
    finishedAt: (row) => row.finishedAt,
    type: (row) => TYPE_LABEL[row.type] ?? row.type,
    durationSeconds: (row) => row.durationSeconds,
    databaseBytes: (row) => row.databaseBytes,
    repositoryBytes: (row) => row.repositoryBytes,
    compressionRatio: (row) => row.compressionRatio,
  };
  const rows = sortRows(
    (repository?.backups ?? []).filter((one: BackupSet) => type === '' || one.type === type),
    sort.direction,
    VALUE[sort.key],
  );

  return (
    <>
      <PageHeader
        title="Zálohy"
        subtitle="Čo je v repozitári a kam sa dá obnoviť. Číta sa priamo z pgBackRest cez zálohovacieho agenta — táto stránka nič nemení a nič nespúšťa."
      />

      <ErrorNotice error={error} onRetry={() => void refetch()} />

      {/*
        The three ways this can be broken, each said separately. "Agent unreachable" and "repository
        unreachable" are different outages with different fixes, and archiving can be failing while both
        are fine — that is the one that silently ends point-in-time recovery (R0147).
      */}
      {data && !data.agentReachable ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Zálohovací agent neodpovedá. O stave záloh sa odtiaľto nedá povedať nič — ani to, že sú v poriadku.
        </Alert>
      ) : null}

      {repository !== null && !repository.isReachable ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Repozitár záloh je nedostupný — pgBackRest hlási: <strong>{repository.statusMessage}</strong> (kód{' '}
          {repository.statusCode}). Agent beží, ale k zálohám sa nedostane.
        </Alert>
      ) : null}

      {status !== null && status.archive.archiveMode !== 'on' ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          Archivácia WAL je vypnutá. Existujúce zálohy sa dajú obnoviť, ale obnova k ľubovoľnému okamihu medzi
          nimi nie je možná.
        </Alert>
      ) : null}

      {status !== null && status.archive.isFailing ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Posledný pokus o archiváciu zlyhal ({status.archive.failedCount} zlyhaní celkovo, naposledy{' '}
          {status.archive.lastFailedWal ?? '—'}). Okno obnovy sa prestalo posúvať.
        </Alert>
      ) : null}

      {status !== null && status.archive.isStale && !status.archive.isFailing ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Posledný WAL segment dorazil pred {duration(status.archive.secondsSinceLastArchive)}. Buď je
          databáza ticho, alebo archivácia zamrzla.
        </Alert>
      ) : null}

      {/*
        The headline. Everything else on this page is detail about how this range came to be, and a backup
        dashboard that does not answer "what would we get back" has not answered anything.
      */}
      {window !== null && window.from !== null ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          Obnoviť sa dá k ľubovoľnému okamihu medzi <strong>{moment(window.from)}</strong> a{' '}
          <strong>{moment(window.to)}</strong> — okno {duration(window.spanSeconds)}.
        </Alert>
      ) : null}

      {repository !== null && repository.isReachable && repository.backups.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Repozitár je dostupný, ale neobsahuje ani jednu zálohu. Obnoviť sa nedá nič.
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatTile
            label="Posledná záloha"
            value={since(rows.length > 0 ? (repository?.backups[0].finishedAt ?? null) : null)}
            hint={rows.length > 0 ? `${TYPE_LABEL[repository!.backups[0].type]} · pred` : 'žiadna'}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatTile
            label="Zálohy v repozitári"
            value={repository?.backups.length ?? 0}
            hint={`${bytes(repository?.repositoryBytes ?? 0)} uložených`}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatTile
            label="Archivované WAL"
            value={status?.archive.archivedCount ?? 0}
            hint={
              status === null
                ? '—'
                : `${status.archive.failedCount} zlyhaní · naposledy pred ${since(status.archive.lastArchivedAt)}`
            }
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatTile
            label="Rozvrh"
            value={status?.scheduleEnabled ? 'zapnutý' : 'vypnutý'}
            hint={
              status === null
                ? '—'
                : `úplná ${status.fullSchedule} · rozdielová ${status.differentialSchedule}`
            }
          />
        </Grid>
      </Grid>

      {repository !== null && repository.isReachable ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Stanza <strong>{repository.stanza}</strong> · PostgreSQL {repository.databaseVersion ?? '—'} ·
          šifrovanie {repository.cipher} · WAL {repository.walMin ?? '—'} → {repository.walMax ?? '—'}
        </Typography>
      ) : null}

      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            select
            label="Typ"
            value={type}
            onChange={(event) => setType(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">všetky</MenuItem>
            <MenuItem value="full">úplná</MenuItem>
            <MenuItem value="diff">rozdielová</MenuItem>
            <MenuItem value="incr">prírastková</MenuItem>
          </TextField>
        </TableFilters>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="finishedAt">
                  Dokončená
                </SortableCell>
                <SortableCell sort={sort} column="type">
                  Typ
                </SortableCell>
                <SortableCell sort={sort} column="durationSeconds" align="right">
                  Trvanie
                </SortableCell>
                <SortableCell sort={sort} column="databaseBytes" align="right">
                  Databáza
                </SortableCell>
                <SortableCell sort={sort} column="repositoryBytes" align="right">
                  Uložené
                </SortableCell>
                <SortableCell sort={sort} column="compressionRatio" align="right">
                  Kompresia
                </SortableCell>
                {/* A WAL range and a parent label are identifiers, not values to order by. */}
                <TableCell>WAL</TableCell>
                <TableCell>Nadväzuje na</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? <TableSkeletonRows columns={8} /> : null}
              {!isLoading &&
                rows.map((row: BackupSet) => (
                  <TableRow key={row.label} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {moment(row.finishedAt)}
                      {row.hasError ? (
                        <Chip size="small" color="error" label="chyba" sx={{ ml: 1 }} variant="outlined" />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.type === 'full' ? 'primary' : 'default'}
                        label={TYPE_LABEL[row.type] ?? row.type}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {duration(row.durationSeconds)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {bytes(row.databaseBytes)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {bytes(row.repositoryBytes)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {row.compressionRatio === null ? '—' : `${Math.round(row.compressionRatio * 100)} %`}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {row.walStart === null
                        ? '—'
                        : `${row.walStart}${row.walStop !== row.walStart ? ` → ${row.walStop}` : ''}`}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                      {row.prior ?? <Chip size="small" variant="outlined" label="samostatná" />}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && rows.length === 0 ? (
          <EmptyState
            title="Žiadne zálohy"
            detail={
              data?.agentReachable === false
                ? 'Agent neodpovedá, takže zoznam sa nedá načítať.'
                : 'Repozitár neobsahuje žiadnu zálohu tohto typu.'
            }
          />
        ) : null}
      </Paper>
    </>
  );
};
